import { getInitials, stringToColor } from '@/utils/stringUtils';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  Avatar,
  AvatarPicture,
  AvatarPopper,
  AvatarWrapper,
  EditHover,
  PopperStack,
} from './styles';
import { EditIcon, ImagePlusIcon, XIcon } from 'lucide-react';
import { useTheme } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Fade from '@mui/material/Fade';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useTranslation } from 'react-i18next';
import type { TransitionProps } from '@mui/material/transitions';
import AlertDialog from '@/components/AlertDialog';
import { enqueueSnackbar } from 'notistack';

function ProfilePicture({
  user,
  imagePreview,
  onFileSelect,
  onRemoveClick,
  setValue,
  setOpenConfirmDialog,
  confirmDialogOpen,
}: {
  user: User;
  imagePreview?: string | null;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveClick: () => void;
  setValue: Dispatch<SetStateAction<boolean>>;
  setOpenConfirmDialog: Dispatch<SetStateAction<boolean>>;
  confirmDialogOpen: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const initials = useMemo(() => getInitials(user.name || ''), [user.name]);
  const bgColor = useMemo(
    () => stringToColor(user.name || Math.random().toString()),
    [user.name]
  );

  const [options, setOptions] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);

  const toggleOptions = () => {
    setOptions((prev) => !prev);
  };

  const closePopper = () => {
    setOptions(false);
  };

  const editOption = () => {
    closePopper();
    fileInputRef.current?.click();
  };

  const removeOption = () => {
    closePopper();
    onRemoveClick();
  };

  // adjust image url if not in correct format
  const getFinalImagePath = (url?: string) => {
    if (!url) return '';

    if (url.startsWith('/') || url.startsWith('uploads')) {
      return url;
    }

    try {
      return new URL(url).pathname;
    } catch (e) {
      console.error(e);
      enqueueSnackbar(t('config.personalData.profileAvatar.invalidUrl'), {
        variant: 'error',
      });
      return url;
    }
  };

  // get profile picture url
  const imgUrl = useMemo(() => {
    if (imagePreview === null) return null;

    if (imagePreview) {
      if (imagePreview.startsWith('blob:')) return imagePreview;

      const previewPath = getFinalImagePath(imagePreview);
      return previewPath ? '/image' + previewPath : imagePreview;
    }

    const imgPath = getFinalImagePath(user.image?.url);
    return imgPath ? '/image' + imgPath : null;
  }, [imagePreview, user.image?.url]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  return (
    <>
      <AvatarWrapper onClick={toggleOptions}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileSelect}
          style={{ display: 'none' }}
          accept="image/png, image/jpeg"
        />

        <EditHover $isSm={isSm}>
          <div ref={itemRef}>
            <EditIcon
              style={{ color: theme.palette.fixed.white, fontSize: 40 }}
            />
          </div>
        </EditHover>

        <Avatar $bgColor={bgColor} $isSm={isSm}>
          {imgUrl ? <AvatarPicture src={imgUrl} $isSm={isSm} /> : initials}
        </Avatar>
      </AvatarWrapper>

      {/** handle avatar popper to remove or change profile picture */}
      <ClickAwayListener
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        onClickAway={closePopper}
      >
        <AvatarPopper
          transition
          open={options}
          anchorEl={itemRef.current}
          placement="bottom-start"
        >
          {({ TransitionProps }: { TransitionProps: TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Stack direction="column">
                <PopperStack onClick={editOption}>
                  <ImagePlusIcon />
                  {t('config.personalData.profileAvatar.change')}
                </PopperStack>
                <PopperStack onClick={removeOption}>
                  <XIcon />
                  {t('config.personalData.profileAvatar.remove')}
                </PopperStack>
              </Stack>
            </Fade>
          )}
        </AvatarPopper>
      </ClickAwayListener>
      {/** confirm to alter profile picture */}
      <AlertDialog
        open={confirmDialogOpen}
        setOpen={setOpenConfirmDialog}
        setValue={setValue}
        title={t('config.personalData.pictureDialog')}
        content={
          <Typography>
            {t('config.personalData.pictureConfirmation')}
          </Typography>
        }
      />
    </>
  );
}

export default ProfilePicture;
